// PatientTable.tsx
import React, { useState, useEffect, forwardRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  SlideProps,
  FormControl,
  Select,
  Typography,
  Box,
  Button,
  InputLabel,
  Divider,
  Paper,
} from "@mui/material";
import { ReusableDataGrid } from "../shared/ReusableDataGrid";
import { toast } from "react-toastify";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

// ==============================
//        INTERFACES
// ==============================
interface Patient {
  id: string;
  avatar: string;
  name: string;
  dob: string;
  gender: string;
  age: number;
  email: string;
  contact: string;
  address: string;

  lastAppointment: string;
  time: string;
  visitType: string;
  reason: string;
  diagnosis: string;
  status: string;

  emergencyContact?: {
    name: string;
    relation: string;
    phone: string;
  };
}

export interface Provider {
  id: string;
  name: string;
  specialty: string;
  clinicName: string;
  clinicAddress: string;
  email: string;
  contactNumber: string;
  availableDays: string;
  officeHours: string;
  profileImage: string;
}

interface ProviderAPIResponse {
  id: string;
  provider_id: string;
  first_name: string;
  last_name: string;
  specialization: string | string[];
  email: string;
  contact_number: string;
  clinic_name: string;
  clinic_address: string;
  available_days: string;
  start_time: string;
  end_time: string;
  profile_image_url: string;
}

// Helper function to format provider API response
const mapProviderData = (apiProvider: ProviderAPIResponse): Provider => {
  const startTime = apiProvider.start_time || "09:00:00";
  const endTime = apiProvider.end_time || "18:00:00";
  const officeHours = `${startTime.split(":")[0]}:${startTime
    .split(":")[1]} - ${endTime.split(":")[0]}:${endTime.split(":")[1]}`;

  // Normalize specialization which can be a string or an array from the API
  const formatEntry = (s: string) =>
    s
      .split(/[_ ]+/)
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(" ");

  let specialty = "";
  if (Array.isArray(apiProvider.specialization)) {
    specialty = apiProvider.specialization.map((s) => formatEntry(s)).join(", ");
  } else if (typeof apiProvider.specialization === "string") {
    specialty = formatEntry(apiProvider.specialization);
  }

  return {
    id: apiProvider.provider_id,
    name: `Dr. ${apiProvider.first_name} ${apiProvider.last_name}`,
    specialty,
    clinicName: apiProvider.clinic_name,
    clinicAddress: apiProvider.clinic_address,
    email: apiProvider.email,
    contactNumber: apiProvider.contact_number,
    availableDays: apiProvider.available_days,
    officeHours,
    profileImage: apiProvider.profile_image_url,
  };
};

// Modal animation
const Transition = forwardRef(function Transition(
  props: SlideProps,
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ==============================
//       MAIN COMPONENT
// ==============================
export default function PatientTable({ refreshKey }: { refreshKey?: number }) {
  const { user } = useAuth();
  const token = user?.token;

  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [rowData, setRowData] = useState<Patient | null>(null);
  const [isReferModalOpen, setIsReferModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );

  const [patients, setPatients] = useState<Patient[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [currentProvider, setCurrentProvider] = useState<Provider | null>(null);
  const [loadingProviders, setLoadingProviders] = useState(false);

  // ==============================
  //   FETCH PROVIDERS & CURRENT PROVIDER
  // ==============================
  const fetchProviders = async () => {
    try {
      if (!token) return;
      setLoadingProviders(true);

      // Fetch all providers
      const allProvidersRes = await axios.get(
        "https://vivotal-backend.vercel.app/api/v1/providers",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (allProvidersRes.data.success) {
        const mappedProviders = allProvidersRes.data.data
          .map((provider: ProviderAPIResponse) => mapProviderData(provider))
          .filter((provider: Provider) => provider.id !== user?.id);

        setProviders(mappedProviders);
      }

      // Fetch current provider details
      if (user?.id) {
        try {
          const currentProviderRes = await axios.get(
            `https://vivotal-backend.vercel.app/api/v1/providers/${user.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (currentProviderRes.data.success) {
            // API may return the provider object under `data`.
            const providerPayload = currentProviderRes.data.data || currentProviderRes.data;
            const current = mapProviderData(providerPayload as any);
            setCurrentProvider(current);
          }
        } catch (err) {
          console.error("Error fetching current provider:", err);
        }
      }
    } catch (err: any) {
      console.error("Error fetching providers:", err);
      toast.error("Failed to load providers");
    } finally {
      setLoadingProviders(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [user, token]);

  // ==============================
  //   FETCH REFERRALS RECEIVED
  // ==============================
  const fetchMappedPatients = useCallback(async () => {
    try {
      if (!user?.id) return;

      const res = await fetch(
        `https://vivotal-backend.vercel.app/api/v1/patients/provider/${user.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const json = await res.json();

      if (!json) throw new Error(json.message);

      const mappedPatients = json.data?.patients || [];

      // ==============================
      //   MAP API → TABLE FORMAT
      // ==============================
      const tableMapped: Patient[] = mappedPatients.map((item: any) => {
        const p = item.patient_details;
        const u = item.user_details;

        return {
          id: item.patient_id, // unique for table
          avatar:
            "https://upload.wikimedia.org/wikipedia/commons/9/99/Sample_User_Icon.png",

          name: `${p.first_name} ${p.last_name}`,
          dob: p.birth_date,
          gender: p.gender,
          age: new Date().getFullYear() - new Date(p.birth_date).getFullYear(),

          contact: p.contact_number,
          email: u.email,
          address: p.address || "-",

          lastAppointment: "N/A",
          time: "-",
          visitType: "N/A",
          reason: "N/A",
          diagnosis: "N/A",
          status: "Active",
          username: u.username,

          emergencyContact: p.emergency_contact_name
            ? {
                name: p.emergency_contact_name,
                relation: p.relationship_to_emergency_contact || "Unknown",
                phone: p.emergency_contact_number || "N/A",
              }
            : undefined,
        };
      });
      setPatients(tableMapped);
    } catch (err: any) {
      toast.error(err.message || "Error fetching patients.");
    }
  }, [user?.id, token]);

  useEffect(() => {
    fetchMappedPatients();
  }, [fetchMappedPatients, refreshKey]);

  // ==============================
  //          MENU HANDLERS
  // ==============================
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLDivElement>,
    data: Patient
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setRowData(data);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ==============================
  //         REFERRAL HANDLING
  // ==============================
  const handleRefer = () => {
    setIsReferModalOpen(true);
    handleMenuClose();
  };

  const handleCloseModal = () => setIsReferModalOpen(false);

  const sendRefer = async () => {
    if (!selectedProvider || !rowData || !currentProvider) {
      toast.error("Missing required information");
      return;
    }

    const payload = {
      patient_id: rowData.id, // ✅ FIXED
      referral_from_provider_id: currentProvider.id,
      referral_to_provider_id: selectedProvider.id,
      reason: "Patient referral",
      notes: `Referred to ${selectedProvider.name} (${selectedProvider.specialty})`,
      status: "Pending",
    };

    try {
      await axios.post(
        "https://vivotal-backend.vercel.app/api/v1/referrals",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json", // ✅ ADD THIS
          },
        }
      );

      toast.success("Referral sent successfully!");
      setSelectedProvider(null);
      handleCloseModal();

      // Refresh patient table data
      await fetchMappedPatients();
    } catch (err: any) {
      console.error(err?.response?.data); // helpful for debugging
      toast.error(err?.response?.data?.message || "Failed to send referral");
    }
  };

  // ==============================
  //            COLUMNS
  // ==============================
  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 120 },

    {
      field: "avatar",
      headerName: "Photo",
      width: 80,
      renderCell: (params) => (
        <img
          src={params.value}
          alt="avatar"
          style={{ width: 40, height: 40, borderRadius: "50%" }}
        />
      ),
    },

    { field: "name", headerName: "Name", width: 180 },
    { field: "contact", headerName: "Contact", width: 140 },
    { field: "email", headerName: "Email", width: 220 },
    { field: "gender", headerName: "Gender", width: 120 },
    { field: "age", headerName: "Age", width: 100 },
    { field: "address", headerName: "Address", width: 220 },

    {
      field: "action",
      headerName: "Action",
      width: 100,
      renderCell: (params) => (
        <div
          onClick={(e) => handleMenuOpen(e, params.row)}
          style={{ cursor: "pointer" }}
        >
          ⋮
        </div>
      ),
    },
  ];

  // ==============================
  //            RENDER
  // ==============================
  return (
    <div style={{ overflowX: "auto" }}>
      <ReusableDataGrid<Patient>
        columns={columns}
        data={patients}
        onRowClick={(params) => {
          navigate("/layout/patient-details", { state: params.row });
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRefer}>Refer To</MenuItem>
      </Menu>

      {/* Referral Modal */}
      <Dialog
        open={isReferModalOpen}
        onClose={handleCloseModal}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            p: 3,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          Refer Patient
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Patient Information Section */}
          {rowData && (
            <Box mb={3}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 2, color: "#333" }}
              >
                Patient Information
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                  border: "1px solid #e8e8e8",
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <img
                    src={rowData.avatar}
                    alt={rowData.name}
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                  <Box>
                    <Typography fontWeight={600} sx={{ color: "#333" }}>
                      {rowData.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#888" }}>
                      Age: {rowData.age} | {rowData.gender}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {rowData.email}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {rowData.contact}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Referral Information Section */}
          <Box mb={3}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 2, color: "#333" }}
            >
              Referral Details
            </Typography>

            {/* From Provider */}
            <Box mb={2.5}>
              <Typography variant="caption" sx={{ color: "#666", mb: 0.5 }}>
                From (Current Provider)
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor: "#f0f7ff",
                  borderRadius: "6px",
                  border: "1px solid #d4e8f7",
                }}
              >
                {currentProvider ? (
                  <>
                    <Typography
                      fontWeight={600}
                      sx={{ color: "#0066cc", mb: 0.5 }}
                    >
                      {currentProvider.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#0066cc", opacity: 0.8 }}
                    >
                      {currentProvider.specialty}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ color: "#0066cc" }}>Loading...</Typography>
                )}
              </Paper>
            </Box>

            {/* To Provider Select */}
            <Box mb={2.5}>
              <FormControl fullWidth disabled={loadingProviders}>
                <InputLabel sx={{ fontWeight: 500 }}>
                  {loadingProviders
                    ? "Loading providers..."
                    : "Refer To (Select Provider)"}
                </InputLabel>
                <Select
                  value={selectedProvider?.name || ""}
                  onChange={(e) =>
                    setSelectedProvider(
                      providers.find((p) => p.name === e.target.value) || null
                    )
                  }
                  label={
                    loadingProviders
                      ? "Loading providers..."
                      : "Refer To (Select Provider)"
                  }
                  sx={{
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#009FF5",
                      },
                    },
                  }}
                >
                  {providers.length === 0 ? (
                    <MenuItem disabled>No providers available</MenuItem>
                  ) : (
                    providers.map((provider) => (
                      <MenuItem key={provider.id} value={provider.name}>
                        <Box>
                          <Typography fontWeight={600} variant="body2">
                            {provider.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            {provider.specialty}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Selected Provider Details */}
            {selectedProvider && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "#f0fff0",
                  borderRadius: "6px",
                  border: "1px solid #d4f5d4",
                }}
              >
                <Typography fontWeight={600} sx={{ color: "#228B22", mb: 1 }}>
                  {selectedProvider.name}
                </Typography>
                <Box
                  sx={{
                    fontSize: "13px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    <strong>Specialty:</strong> {selectedProvider.specialty}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    <strong>Clinic:</strong> {selectedProvider.clinicName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    <strong>Address:</strong> {selectedProvider.clinicAddress}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    <strong>Hours:</strong> {selectedProvider.officeHours}
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCloseModal}
              sx={{
                borderColor: "#ddd",
                color: "#666",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderColor: "#999",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={sendRefer}
              disabled={!selectedProvider}
              sx={{
                background: "linear-gradient(135deg, #009FF5 0%, #0088cc 100%)",
                fontWeight: 600,
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "15px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0088cc 0%, #007aaa 100%)",
                },
                "&:disabled": {
                  background: "#ccc",
                },
              }}
            >
              Send Referral
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}
